import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Sensor } from '@models';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { MapDetails } from '@models/MapFavorite/mapDetails.model';
import { MapFavoriteInfo } from '@models/MapFavorite/mapFavoriteInfo.model';
import { SensorService } from '@services/sensor.service';
import { CommonShared } from 'app/shared/classes/common';
import { decodeDateRange, presetDateRange } from 'app/shared/classes/dateRangeCodec';
import { SelectionReadingsLoader } from 'app/shared/classes/selectionReadingsLoader';
import { Subscription } from 'rxjs';

// A map favorite shown read-only (a dashboard tile or its enlarged view): it loads the
// readings of the favorite's sensors, reading types and (rolling) date range, then
// renders the shared map with the saved view, open pin popups and labels.
//
// Readings are loaded once per favorite (no polling); label values refresh on their own
// timer inside the map. The map's changes are not reported, so panning a dashboard tile
// never saves anything.
@Component({
  standalone: false,
  selector: 'kig-map-favorite-tile',
  templateUrl: './map-favorite-tile.component.html',
  styleUrls: ['./map-favorite-tile.component.scss']
})
export class MapFavoriteTileComponent implements OnChanges, OnDestroy {
  @Input() mapFavorite: MapFavoriteInfo;
  @Input() height = '400px';

  sensors: Sensor[] = [];
  readings: SensorReadingInfo[] = [];
  savedMapDetails: MapDetails | null = null;

  loading = false;
  failed = false;

  private common = new CommonShared();
  private loader: SelectionReadingsLoader;
  private subscription: Subscription | null = null;

  constructor(private sensorService: SensorService) {
    this.loader = new SelectionReadingsLoader(sensorService);
  }


  get noReadings(): boolean {
    return !this.loading && !this.failed && this.readings.length === 0;
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mapFavorite'] != undefined) {
      this.load();
    }
  }


  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.loader.cancel();
  }


  private load(): void {
    this.subscription?.unsubscribe();
    this.loader.clear();

    const favorite = this.mapFavorite;
    this.readings = [];
    this.failed = false;

    if (favorite == undefined) {
      this.sensors = [];
      this.savedMapDetails = null;
      return;
    }

    let details: MapDetails | null = null;
    try {
      details = (favorite.mapDetails != undefined && favorite.mapDetails !== '') ? JSON.parse(favorite.mapDetails) : null;
    } catch {
      details = null;
    }
    this.savedMapDetails = details;

    const range = decodeDateRange(favorite.dateRange)?.range ?? presetDateRange('Last 3 Days');
    const [timeZone, timeZoneOffset] = this.common.setTimeZoneSelection(details?.timeZone ?? 'Local');

    this.loading = true;

    // The favorite's own sensors do not carry their GPS location (the fallback position
    // of a pin), so use the full sensor records (cached, shared with the selector).
    this.subscription = this.sensorService.getAllSensorsWithoutTypes().subscribe(
      (allSensors: Sensor[]) => {
        const ids = new Set((favorite.sensors ?? []).map(s => s.id));
        this.sensors = (allSensors ?? []).filter(s => ids.has(s.id));
        this.sensors.forEach(s => {
          if (s.name == null || s.name === '') {
            s.name = s.uuid;
          }
          s.displayedSensorName = this.common.getSensorDisplayName(s);
        });

        this.subscription = this.loader.load({
          sensors: this.sensors,
          readingTypes: favorite.sensorReadingTypes ?? [],
          dateRange: range,
          timeZone,
          timeZoneOffset
        }).subscribe(
          result => {
            if (result == null) {
              return;   // superseded
            }
            this.readings = result.readings;
            this.loading = false;
          },
          () => {
            this.loading = false;
            this.failed = true;
          });
      },
      () => {
        this.loading = false;
        this.failed = true;
      });
  }
}
